import { Language } from '../services/preferences.types';

/**
 * Language code -> the desktop's dictionary name.
 *
 * The dictionary FILENAME is the desktop's, not a locale code, because web.ui's
 * dictionaries are supersets of the desktop's and the two products are kept in
 * step by hand. Renaming them to en/nl/fr here would mean maintaining a mapping
 * in both repos and would make "is this the same file the desktop ships?" a
 * question rather than an observation.
 *
 * This lived privately inside ProjectService, which needed it to label a
 * project's language. It is shared now because the translation runtime needs the
 * same mapping, and two copies of a lookup like this drift the moment a seventh
 * language is added.
 */
export const LANGUAGE_FILE: Record<Language, string> = {
  en: 'English',
  nl: 'Dutch',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  es: 'Spanish',
};

/**
 * What a language is CALLED, in itself.
 *
 * Endonyms, per the standing ruling: a reader looking for their own language
 * finds "Deutsch" faster than "German", and someone who cannot read the current
 * UI language can still find their own — which is exactly the person using a
 * language picker.
 *
 * SEPARATE FROM LANGUAGE_FILE ON PURPOSE, and this is not tidiness. A display
 * label, a dictionary filename and a language ID are three different facts that
 * happen to look alike, and they already disagree: the desktop's Spanish file
 * declares LanguageID "SP" where the ISO code is "es". An earlier version of this
 * file aliased the label map to the filename map because their values happened to
 * coincide in English, which would have put "English", "Dutch", "French" into a
 * picker meant to show endonyms the moment anyone read it as a label.
 *
 * (The desktop's own files are half-converted already — its German dictionary is
 * named "Deutsch" while the other five carry English names.)
 */
export const LANGUAGE_ENDONYM: Record<Language, string> = {
  en: 'English',
  nl: 'Nederlands',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  es: 'Español',
};

/** The language every lookup falls back to, and the only one guaranteed complete. */
export const FALLBACK_LANGUAGE: Language = 'en';

/** Codes the app can resolve. Anything else falls back rather than 404s. */
export function isSupportedLanguage(code: string | null | undefined): code is Language {
  return !!code && Object.prototype.hasOwnProperty.call(LANGUAGE_FILE, code);
}
